import api from './api';
import { ApiResponse, PaginatedResponse } from '../types';
import { GeneratedImage, ImageGenerateParams, ImageFilters, ImageUsageStats } from '../types/imageStudio';
import { pollJobUntilComplete } from './aiJobService';

export const imageStudioService = {
  async getImages(filters?: ImageFilters): Promise<PaginatedResponse<GeneratedImage>> {
    const response = await api.get('/image-studio', { params: filters });
    return response.data;
  },

  async getImage(id: string): Promise<ApiResponse<GeneratedImage>> {
    const response = await api.get(`/image-studio/${id}`);
    return response.data;
  },

  /**
   * Generate image using async background processing.
   * Sends the request with async: true, then polls until the job completes,
   * and returns the completed GeneratedImage record.
   */
  async generateImage(params: ImageGenerateParams, onProgress?: (progress: number) => void): Promise<ApiResponse<GeneratedImage>> {
    // Always use async mode to avoid HTTP timeout (image generation takes 30-90s)
    const response = await api.post('/image-studio/generate', { ...params, async: true });
    const result = response.data;

    // If backend returned async response, poll for completion
    if (result.async && result.jobId) {
      const imageId = result.data?.id;

      if (!imageId) {
        throw new Error('Server did not return image ID');
      }

      // Poll until the background job finishes (5 min timeout — covers OpenAI latency + queue wait)
      await pollJobUntilComplete(
        result.jobId,
        (progress) => {
          if (onProgress) onProgress(progress);
        },
        2500, // poll every 2.5s
        300000 // 5 minute timeout
      );

      // Job completed — fetch the updated image record with final URL
      // Small delay to ensure DB write from worker is committed
      await new Promise(r => setTimeout(r, 500));

      // Retry fetch up to 3 times in case of race condition between worker DB write and our read
      for (let attempt = 0; attempt < 3; attempt++) {
        const imageResponse = await api.get(`/image-studio/${imageId}`);
        const imageData: ApiResponse<GeneratedImage> = imageResponse.data;

        if (imageData.success && imageData.data) {
          if (imageData.data.status === 'failed') {
            throw new Error(imageData.data.error_message || 'Image generation failed');
          }
          if (imageData.data.original_url) {
            return imageData; // Success — image has URL
          }
        }

        // Image not ready yet, wait and retry
        if (attempt < 2) {
          await new Promise(r => setTimeout(r, 1000));
        }
      }

      throw new Error('Image generation completed but no image URL was returned');
    }

    // If backend ran synchronously (e.g. async not supported), return directly
    return result;
  },

  async uploadImage(file: File, data?: { title?: string; campaign_id?: string; tags?: string[] }): Promise<ApiResponse<GeneratedImage>> {
    const formData = new FormData();
    formData.append('file', file);
    if (data?.title) formData.append('title', data.title);
    if (data?.campaign_id) formData.append('campaign_id', data.campaign_id);
    if (data?.tags) formData.append('tags', JSON.stringify(data.tags));

    const response = await api.post('/image-studio/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  /**
   * Upload a reference image for AI generation.
   * Goes to S3 only — does NOT create a gallery record.
   */
  async uploadReference(file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/image-studio/upload-reference', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    const url = response.data?.data?.url;
    if (!url) throw new Error('Reference upload returned no URL');
    return { url };
  },

  async removeBackground(id: string): Promise<ApiResponse<GeneratedImage>> {
    const response = await api.post(`/image-studio/${id}/remove-background`);
    return response.data;
  },

  async saveEdited(id: string, data: { url?: string; file?: File; title?: string; width?: number; height?: number; mime_type?: string }): Promise<ApiResponse<GeneratedImage>> {
    if (data.file) {
      const formData = new FormData();
      formData.append('file', data.file);
      if (data.title) formData.append('title', data.title);
      if (data.width) formData.append('width', String(data.width));
      if (data.height) formData.append('height', String(data.height));
      if (data.mime_type) formData.append('mime_type', data.mime_type);

      const response = await api.post(`/image-studio/${id}/save-edited`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    }

    const response = await api.post(`/image-studio/${id}/save-edited`, data);
    return response.data;
  },

  async updateImage(id: string, data: { title?: string; tags?: string[]; is_favorite?: boolean; campaign_id?: string | null }): Promise<ApiResponse<GeneratedImage>> {
    const response = await api.put(`/image-studio/${id}`, data);
    return response.data;
  },

  async deleteImage(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/image-studio/${id}`);
    return response.data;
  },

  async getUsageStats(): Promise<ApiResponse<ImageUsageStats>> {
    const response = await api.get('/image-studio/stats');
    return response.data;
  },

  async aiEdit(params: { prompt: string; mode: 'edit' | 'generate'; imageBase64?: string; style?: string }): Promise<ApiResponse<any>> {
    const response = await api.post('/image-studio/ai-edit', params);
    return response.data;
  }
};
